<template>
  <v-dialog v-model="dialog" max-width="1200" scrollable>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon left :color="getFileTypeColor(fileName)">
          {{ getFileTypeIcon(fileName) }}
        </v-icon>
        <span class="text-h6">{{ fileName }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0">
        <!-- Loading State -->
        <div v-if="loading" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <p class="mt-4 text-body-1">Loading file preview...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center pa-8">
          <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
          <h3 class="text-h6 mb-2 text-error">Failed to load file</h3>
          <p class="text-body-2 text-grey-600">{{ error }}</p>
          <v-btn color="primary" @click="loadPreview" class="mt-4">
            <v-icon left>mdi-refresh</v-icon>
            Try Again
          </v-btn>
        </div>

        <!-- Preview Content -->
        <div v-else-if="previewData.length > 0">
          <!-- Search and Controls -->
          <v-card-text class="pb-2">
            <v-row align="center">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="searchTerm"
                  prepend-icon="mdi-magnify"
                  label="Search in table..."
                  clearable
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="text-right">
                <v-chip small color="info" outlined class="mr-2">
                  {{ filteredData.length }} of {{ previewData.length }} rows
                </v-chip>
                <v-chip small color="success" outlined> {{ headers.length }} columns </v-chip>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- Data Table -->
          <v-data-table
            :headers="headers"
            :items="filteredData"
            :items-per-page="itemsPerPage"
            :search="searchTerm"
            class="elevation-0 certifai-table"
            dense
            fixed-header
            height="500"
            :footer-props="{
              itemsPerPageText: 'Rows per page:',
              itemsPerPageAllText: 'All',
              nextIcon: 'mdi-chevron-right',
              prevIcon: 'mdi-chevron-left',
              firstIcon: 'mdi-page-first',
              lastIcon: 'mdi-page-last',
              itemsPerPageOptions: [5, 10, 25, 50, 100, -1],
            }"
          >
            <template v-slot:item="{ item }">
              <tr>
                <td v-for="header in headers" :key="header.value" class="text-caption">
                  <div class="cell-content">
                    {{ getCellValue(item, header.value) }}
                  </div>
                </td>
              </tr>
            </template>

            <template v-slot:no-data>
              <div class="text-center pa-4">
                <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-table-search</v-icon>
                <p class="text-grey-600">No data matches your search</p>
              </div>
            </template>

            <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
              {{ pageStart }}-{{ pageStop }} of {{ itemsLength }}
            </template>
          </v-data-table>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center pa-8">
          <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-file-table</v-icon>
          <h3 class="text-h6 mb-2 text-grey-600">No data to display</h3>
          <p class="text-body-2 text-grey-500">
            The file appears to be empty or has no readable data
          </p>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="px-6 py-4">
        <v-chip small color="primary" outlined class="mr-2">
          <v-icon left small>mdi-file-document</v-icon>
          {{ getFileType(fileName) }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="downloadFile" :loading="downloading">
          <v-icon left>mdi-download</v-icon>
          Download
        </v-btn>
        <v-btn color="grey darken-1" text @click="closeDialog">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

export default {
  name: 'FilePreviewDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    file: {
      type: Object,
      default: () => ({}),
    },
    onDownload: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      previewData: [],
      headers: [],
      searchTerm: '',
      itemsPerPage: 25,
      downloading: false,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    fileName() {
      return this.file?.fileName || 'Unknown File';
    },
    filteredData() {
      if (!this.searchTerm) return this.previewData;

      const term = this.searchTerm.toLowerCase();
      return this.previewData.filter(row =>
        Object.values(row).some(value =>
          String(value)
            .toLowerCase()
            .includes(term),
        ),
      );
    },
  },
  watch: {
    dialog(newVal) {
      if (newVal && this.file) {
        this.loadPreview();
      } else if (!newVal) {
        this.resetData();
      }
    },
  },
  methods: {
    async loadPreview() {
      if (!this.file?.filePath) return;

      this.loading = true;
      this.error = null;
      this.previewData = [];
      this.headers = [];

      try {
        // Emit event to parent to download file data
        this.$emit('load-file', this.file);
      } catch (error) {
        console.error('Error loading file preview:', error);
        this.error = 'Failed to load file preview. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async processFileData(fileBlob) {
      const fileName = this.fileName.toLowerCase();

      if (fileName.endsWith('.csv')) {
        await this.parseCSV(fileBlob);
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        await this.parseExcel(fileBlob);
      } else {
        this.error = 'Unsupported file format for preview';
      }
    },

    async parseCSV(fileBlob) {
      const text = await fileBlob.text();

      const { data, errors } = Papa.parse(text, {
        header: false,
        skipEmptyLines: true,
      });

      if (!data || data.length === 0) {
        this.error = 'CSV file is empty or malformed';
        return;
      }

      const headerValues = data[0];
      this.headers = headerValues.map((header, index) => ({
        text: header || `Column ${index + 1}`,
        value: `col_${index}`,
        sortable: true,
        width: 150,
      }));

      this.previewData = data.slice(1).map((rowValues, rowIndex) => {
        const row = { _rowIndex: rowIndex + 1 };
        rowValues.forEach((value, colIndex) => {
          row[`col_${colIndex}`] = value || '';
        });
        return row;
      });

      if (errors.length > 0) {
        console.warn('Some CSV parsing errors occurred:', errors);
      }
    },

    async parseExcel(fileBlob) {
      const arrayBuffer = await fileBlob.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      // Get first worksheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length === 0) {
        this.error = 'Excel file is empty';
        return;
      }

      // Parse headers
      const headerRow = jsonData[0] || [];
      this.headers = headerRow.map((header, index) => ({
        text: header || `Column ${index + 1}`,
        value: `col_${index}`,
        sortable: true,
        width: 150,
      }));

      // Parse data rows
      this.previewData = jsonData.slice(1).map((row, rowIndex) => {
        const rowData = { _rowIndex: rowIndex + 1 };

        row.forEach((value, colIndex) => {
          rowData[`col_${colIndex}`] = value !== undefined ? value : '';
        });

        return rowData;
      });
    },

    getCellValue(item, key) {
      const value = item[key];
      if (value === null || value === undefined) return '';
      return String(value);
    },

    getFileType(fileName) {
      return (
        fileName
          .split('.')
          .pop()
          ?.toUpperCase() || 'FILE'
      );
    },

    getFileTypeIcon(fileName) {
      const extension = fileName
        .split('.')
        .pop()
        ?.toLowerCase();
      const icons = {
        xlsx: 'mdi-file-excel',
        xls: 'mdi-file-excel',
        csv: 'mdi-file-delimited',
      };
      return icons[extension] || 'mdi-file';
    },

    getFileTypeColor(fileName) {
      const extension = fileName
        .split('.')
        .pop()
        ?.toLowerCase();
      const colors = {
        xlsx: 'green',
        xls: 'green',
        csv: 'blue',
      };
      return colors[extension] || 'grey';
    },

    async downloadFile() {
      if (this.onDownload && this.file) {
        this.downloading = true;
        try {
          await this.onDownload(this.file);
        } catch (error) {
          console.error('Download failed:', error);
        } finally {
          this.downloading = false;
        }
      }
    },

    closeDialog() {
      this.dialog = false;
    },

    resetData() {
      this.previewData = [];
      this.headers = [];
      this.searchTerm = '';
      this.error = null;
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.cell-content {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v-data-table >>> .v-data-table__wrapper {
  border: 1px solid #e0e0e0;
}

.v-data-table >>> th {
  background-color: #f5f5f5 !important;
  font-weight: 600 !important;
}

.v-data-table >>> td {
  border-bottom: 1px solid #f0f0f0 !important;
}

.v-data-table >>> tr:hover {
  background-color: #f8f9fa !important;
}
</style>
